import { useState } from 'react';
import type {
  HeatDemandData,
  DwellingForm,
  FabricEra,
  RetrofitLevel,
  ConstructionArchetype,
} from '../types';
import { deriveHeatLossBand } from '../derivations';

const DWELLING_OPTIONS: { value: DwellingForm; label: string }[] = [
  { value: 'detached', label: 'Detached' },
  { value: 'semi-detached', label: 'Semi-detached' },
  { value: 'mid-terrace', label: 'Mid-terrace' },
  { value: 'end-terrace', label: 'End-terrace' },
  { value: 'flat-top', label: 'Flat (top floor)' },
  { value: 'flat-mid', label: 'Flat (mid floor)' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'unknown', label: 'Unknown' },
];

const ERA_OPTIONS: { value: FabricEra; label: string }[] = [
  { value: 'pre-1919', label: 'Pre-1919' },
  { value: '1919-1944', label: '1919–1944' },
  { value: '1945-1964', label: '1945–1964' },
  { value: '1965-1980', label: '1965–1980' },
  { value: '1981-1995', label: '1981–1995' },
  { value: '1996-2006', label: '1996–2006' },
  { value: '2007-2013', label: '2007–2013' },
  { value: '2013-present', label: '2013 – present' },
  { value: 'unknown', label: 'Unknown' },
];

const RETROFIT_OPTIONS: { value: RetrofitLevel; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'partial', label: 'Partial (loft or windows)' },
  { value: 'full', label: 'Full (walls + loft + windows)' },
  { value: 'unknown', label: 'Unknown' },
];

const ARCHETYPE_OPTIONS: { value: ConstructionArchetype; label: string }[] = [
  { value: 'standard', label: 'Standard masonry / cavity' },
  { value: 'timber-frame', label: 'Timber frame' },
  { value: 'concrete-panel', label: 'Non-trad concrete panel' },
  { value: 'prefab', label: 'Post-war prefab' },
  { value: 'park-home', label: 'Park home' },
  { value: 'solid-wall', label: 'Solid wall (pre-cavity)' },
  { value: 'unknown', label: 'Unknown' },
];

interface Props {
  data: HeatDemandData;
  onSave: (data: HeatDemandData) => void;
  onClose: () => void;
}

export function HeatDemandScreen({ data, onSave, onClose }: Props) {
  const [draft, setDraft] = useState<HeatDemandData>({ ...data });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const derivedBand = deriveHeatLossBand(draft);

  const set = <K extends keyof HeatDemandData>(key: K, value: HeatDemandData[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  };

  const handleSave = () => {
    setHasUnsavedChanges(false);
    onSave({ ...draft, heatLossBand: derivedBand });
  };

  return (
    <>
      <div className="hub-screen-overlay">
      <div className="hub-screen" onClick={e => e.stopPropagation()}>
        <div className="hub-screen__header">
          <h2 className="hub-screen__title">Heat Demand</h2>
          <button className="hub-screen__close" onClick={handleClose}>&times;</button>
        </div>

        <div className="hub-screen__body">
          <div className="hub-field">
            <label className="hub-field__label">Dwelling form (exposure proxy)</label>
            <div className="hub-field__chip-group">
              {DWELLING_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.dwellingForm === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('dwellingForm', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Bedrooms (size proxy)</label>
            <select
              value={draft.bedrooms}
              onChange={e => set('bedrooms', e.target.value)}
            >
              <option value="">—</option>
              {['1', '2', '3', '4', '5', '6+'].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Bathrooms</label>
            <select
              value={draft.bathrooms}
              onChange={e => set('bathrooms', e.target.value)}
            >
              <option value="">—</option>
              {['1', '2', '3', '4', '5+'].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Fabric era (Part L regime bands)</label>
            <div className="hub-field__chip-group">
              {ERA_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.fabricEra === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('fabricEra', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Retrofit level</label>
            <div className="hub-field__chip-group">
              {RETROFIT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.retrofitLevel === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('retrofitLevel', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Construction archetype</label>
            <span className="hub-field__hint">Only relevant for non-standard construction types</span>
            <div className="hub-field__chip-group">
              {ARCHETYPE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.constructionArchetype === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('constructionArchetype', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Known heat load / heat loss (kW)</label>
            <span className="hub-field__hint">
              If a heat loss calculation has been performed, enter the result here (optional)
            </span>
            <input
              type="number"
              min="0"
              max="200"
              step="0.1"
              placeholder="e.g. 8.5"
              value={draft.knownHeatLossKw ?? ''}
              onChange={e => set('knownHeatLossKw', e.target.value === '' ? undefined : parseFloat(e.target.value))}
            />
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Heat loss band (auto-derived)</label>
            <input type="text" value={derivedBand === 'unknown' ? 'Not enough inputs yet' : derivedBand} readOnly />
            {draft.knownHeatLossKw !== undefined && draft.knownHeatLossKw > 0 && (
              <span className="hub-field__hint">Manual heat loss entered. Confidence: High (manual entry)</span>
            )}
          </div>
        </div>

        <div className="hub-screen__footer">
          <button className="hub-screen__button hub-screen__button--secondary" onClick={handleClose}>Cancel</button>
          <button className="hub-screen__button hub-screen__button--primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>

    {/* Discard changes dialog */}
    {showDiscardDialog && (
      <div className="hub-screen-overlay" style={{ zIndex: 1001 }}>
        <div className="hub-screen hub-screen--dialog" onClick={e => e.stopPropagation()}>
          <div className="hub-screen__header">
            <h2 className="hub-screen__title">Discard changes?</h2>
          </div>
          <div className="hub-screen__body">
            <p>You have unsaved changes. Are you sure you want to discard them?</p>
          </div>
          <div className="hub-screen__footer">
            <button 
              className="hub-screen__button hub-screen__button--secondary" 
              onClick={() => setShowDiscardDialog(false)}
            >
              Keep editing
            </button>
            <button 
              className="hub-screen__button hub-screen__button--danger" 
              onClick={() => {
                setShowDiscardDialog(false);
                setHasUnsavedChanges(false);
                onClose();
              }}
            >
              Discard changes
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}
